/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
*
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
*/

import type { JSX } from 'react';

import {
  $isCodeNode,
  CODE_LANGUAGE_MAP,
} from '@lexical/code';
import { $createHorizontalRuleNode } from '@lexical/extension';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $isListNode, ListNode } from '@lexical/list';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';
import { $isHeadingNode } from '@lexical/rich-text';
import {
  $getSelectionStyleValueForProperty,
  $isParentElementRTL,
} from '@lexical/selection';
import { $isTableNode, $isTableSelection } from '@lexical/table';
import {
  $dfs,
  $findMatchingParent,
  $getNearestNodeOfType,
  $insertNodeToNearestRoot,
  IS_APPLE,
  mergeRegister,
} from '@lexical/utils';
import clsx from 'clsx';
import {
  $createTextNode,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_EDITOR,
  ElementFormatType,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  TextNode,
  UNDO_COMMAND
} from 'lexical';
import React, { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import { useOnClickOutside } from 'usehooks-ts';

import { SystemPopover } from '@cms/components/data/Popover';
import { EditorKey } from '@cms/utils/contexts/editorSettings';
import { useEditorSettings } from '../../../../utils/contexts/EditorSettingsContext';
import {
  blockTypeToBlockName,
  fontSizes,
  useToolbarState,
} from '../../../../utils/contexts/ToolbarContext';
import { Button } from '../../../data/Button';
import { SystemTextInput } from '../../../data/TextInput';
import { SystemTooltip } from '../../../ui/Tooltip';
import { DropDown, DropDownItem } from '../../ui/DropDown';
import { getSelectedNode } from '../../utils/getSelectedNode';
import { sanitizeUrl } from '../../utils/url';
import { SHORTCUTS } from '../ShortcutsPlugin/shortcuts';
import { formatBulletList, formatHeading, formatNumberedList, formatParagraph, updateFontSizeInEditor } from './utils';

import { FaRedoAlt, FaUndoAlt } from "react-icons/fa";
import { MdFindReplace, MdFormatAlignCenter, MdFormatAlignLeft, MdFormatAlignRight, MdFormatBold, MdFormatItalic, MdFormatListBulleted, MdFormatListNumbered, MdFormatUnderlined, MdInsertLink } from 'react-icons/md';
import { RiOmega } from 'react-icons/ri';

const getDropDownActiveClass = (active: boolean) => {
  if (active) {
    return 'active dropdown-item-active';
  } else {
    return '';
  }
}

type USED_ELEMENT_ALIGN_TYPES = Exclude<ElementFormatType, '' | 'end' | 'justify' | 'start'>

const ELEMENT_ALIGN_OPTIONS: {
  [key in USED_ELEMENT_ALIGN_TYPES]: {
    name: string;
    iconComponent: React.ReactNode;
  };
} = {
  center: {
    name: 'Center Align',
    iconComponent: <MdFormatAlignCenter size={22} />
  },
  left: {
    name: 'Left Align',
    iconComponent: <MdFormatAlignLeft size={24} />
  },
  right: {
    name: 'Right Align',
    iconComponent: <MdFormatAlignRight size={24} />
  },
};

function ElementAlignDropdown({
  editor,
  value,
  disabled = false,
}: {
  editor: LexicalEditor;
  value: USED_ELEMENT_ALIGN_TYPES;
  isRTL: boolean;
  disabled: boolean;
}) {
  const formatOption = ELEMENT_ALIGN_OPTIONS[value || 'left'];

  return null

  return (
    <DropDown
      disabled={disabled}
      buttonLabel={formatOption.iconComponent}
      buttonClassName={clsx(
        "toolbar-item cs-dropdown spaced alignment",
      )}
      buttonAriaLabel="Formatting options for text alignment"
    >
      <DropDownItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
        }}
        className="item"
      >
        <MdFormatAlignLeft size={24} />
      </DropDownItem>
      <DropDownItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
        }}
        className="item"
      >
        <MdFormatAlignCenter size={24} />
      </DropDownItem>
      <DropDownItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
        }}
        className="item"
      >
        <MdFormatAlignRight size={24} />
      </DropDownItem>
    </DropDown>
  );
}

function BlockHeadingDropDown({
  editor,
  blockType,
  disabled = false,
}: {
  blockType: keyof typeof blockTypeToBlockName;
  editor: LexicalEditor;
  disabled?: boolean;
}): JSX.Element {
  return (
    <DropDown
      disabled={disabled}
      buttonClassName="toolbar-item block-controls"
      buttonIconClassName={'icon block-type ' + blockType}
      buttonLabel={blockTypeToBlockName[blockType]}
      buttonAriaLabel="Formatting options for text style"
    >
      <DropDownItem
        className={'item wide ' + getDropDownActiveClass(blockType === 'h2')}
        onClick={() => formatHeading(editor, blockType, 'h2')}>
        <div className="icon-text-container">
          <span className="EditorTheme__h2 text">Heading 2</span>
        </div>
        {/* <span className="shortcut">{SHORTCUTS.HEADING2}</span> */}
      </DropDownItem>
      <DropDownItem
        className={'item wide ' + getDropDownActiveClass(blockType === 'h3')}
        onClick={() => formatHeading(editor, blockType, 'h3')}>
        <div className="icon-text-container">
          <span className="EditorTheme__h3 text">Heading 3</span>
        </div>
        {/* <span className="shortcut">{SHORTCUTS.HEADING3}</span> */}
      </DropDownItem>
      <DropDownItem
        className={'item wide ' + getDropDownActiveClass(blockType === 'h4')}
        onClick={() => formatHeading(editor, blockType, 'h4')}>
        <div className="icon-text-container">
          <span className="EditorTheme__h4 text">Heading 4</span>
        </div>
        {/* <span className="shortcut">{SHORTCUTS.HEADING4}</span> */}
      </DropDownItem>
      <DropDownItem
        className={
          'item wide ' + getDropDownActiveClass(blockType === 'paragraph')
        }
        onClick={() => formatParagraph(editor)}>
        <div className="icon-text-container">
          <span className="text">Normal text</span>
        </div>
        {/* <span className="shortcut">{SHORTCUTS.NORMAL}</span> */}
      </DropDownItem>
    </DropDown>
  );
}

function FontSizeDropDown({
  editor,
  selectionFontSize,
  disabled = false,
}: {
  selectionFontSize: keyof typeof fontSizes;
  editor: LexicalEditor;
  disabled?: boolean;
}): JSX.Element {
  return (
    <DropDown
      disabled={disabled}
      buttonClassName="toolbar-item block-controls"
      buttonIconClassName={'icon block-type '}
      buttonLabel={fontSizes[selectionFontSize || '16'] || fontSizes['16']}
      buttonAriaLabel="Formatting options for text style"
    >
      <DropDownItem
        className={'item wide ' + getDropDownActiveClass(selectionFontSize === '14')}
        onClick={() => updateFontSizeInEditor(editor, '14')}>
        <div className="icon-text-container">
          <span className="text text-[14px]">{fontSizes['14']}</span>
        </div>
      </DropDownItem>
      <DropDownItem
        className={'item wide ' + getDropDownActiveClass(selectionFontSize === '16')}
        onClick={() => updateFontSizeInEditor(editor, '16')}>
        <div className="icon-text-container">
          <span className="text text-[16px]">{fontSizes['16']}</span>
        </div>
      </DropDownItem>
      <DropDownItem
        className={'item wide ' + getDropDownActiveClass(selectionFontSize === '18')}
        onClick={() => updateFontSizeInEditor(editor, '18')}>
        <div className="icon-text-container">
          <span className="text text-[18px]">{fontSizes['18']}</span>
        </div>
      </DropDownItem>
    </DropDown>
  );
}

function FindAndReplaceButton({
  editor,
  disabled = false,
}: {
  editor: LexicalEditor;
  disabled?: boolean;
}): JSX.Element {
  const [showPopup, setShowPopup] = useState(false);
  const findAndReplaceButtonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [replaceTerm, setReplaceTerm] = useState('');

  const handleClick = useCallback(() => {
    if (findAndReplaceButtonRef.current) {
      setShowPopup(!showPopup);
    }
  }, [findAndReplaceButtonRef]);

  const handleClear = useCallback(() => {
    editor.update(() => {
      const nodes = $dfs();

      nodes.forEach(({ node }) => {
        if (node instanceof TextNode) {
          node.setStyle("background-color: transparent;");
        }
      });
    });

    setSearchTerm("");
    setReplaceTerm("");
  }, [editor]);

  const handleFind = useCallback(() => {
    editor.update(() => {
      const term = searchTerm.trim();
      if (!term) return;

      const regex = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/ig, "\\$&"), "gi");
      const nodes = $dfs();

      nodes.forEach(({ node }) => {
        if (!(node instanceof TextNode)) return;

        const text = node.getTextContent();
        node.setStyle("background-color: transparent;"); // clear old highlights

        const matches = [...text.matchAll(regex)];
        if (matches.length === 0) return;

        // split node around each match and highlight only the matches
        let lastIndex = 0;
        const newNodes = [];

        for (const match of matches) {
          const start = match.index!;
          const end = start + match[0].length;

          if (start > lastIndex) {
            newNodes.push($createTextNode(text.slice(lastIndex, start)));
          }

          const highlightNode = $createTextNode(text.slice(start, end));
          highlightNode.setStyle("background-color: yellow;");
          newNodes.push(highlightNode);

          lastIndex = end;
        }

        if (lastIndex < text.length) {
          newNodes.push($createTextNode(text.slice(lastIndex)));
        }

        // replace original text node with split + highlighted ones
        node.replace(newNodes[0]);
        let prev = newNodes[0];
        for (let i = 1; i < newNodes.length; i++) {
          prev.insertAfter(newNodes[i]);
          prev = newNodes[i];
        }
      });
    });
  }, [editor, searchTerm]);

  const handleReplace = useCallback(() => {
    editor.update(() => {
      const nodes = $dfs();

      nodes.forEach(({ node }) => {
        if (!(node instanceof TextNode)) return;

        const text = node.getTextContent();
        node.setStyle("background-color: transparent;");

        if (text.includes(searchTerm)) {
          const replaced = text.replaceAll(searchTerm, replaceTerm);
          node.setTextContent(replaced);
        }
      });
    });

    setSearchTerm("");
    setReplaceTerm("");
  }, [editor, searchTerm, replaceTerm]);

  const handleClickOutside = () => {
    handleClear()
    setShowPopup(false);
  }

  useEffect(() => {
    return () => {
      setSearchTerm('');
      setReplaceTerm('');
    }
  }, [])

  useOnClickOutside(popupRef as React.RefObject<HTMLElement>, handleClickOutside)

  return (
    <div className='relative toolbar-item spaced'>
      <ToolButton
        tooltip='Find and replace'
        onClick={handleClick}
        className=""
        aria-label="Find and replace"
        // title={`Find and replace`}
        ref={findAndReplaceButtonRef}
        disabled={disabled}
      >
        <MdFindReplace size={24} />
      </ToolButton>

      {showPopup ? (
        <div
          ref={popupRef}
          className='absolute top-full left-0 z-40 bg-white p-2 rounded shadow'
        >
          <div className='flex flex-col gap-2'>
            <SystemTextInput inputSize='full' placeholder='Search term' value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            <SystemTextInput inputSize='full' placeholder='Replace term' value={replaceTerm} onChange={(e) => setReplaceTerm(e.target.value)} />

            <div className='self-end flex items-center gap-2'>
              <Button onClick={handleClear}>
                Clear
              </Button>
              <Button onClick={handleFind}>
                Find
              </Button>
              <Button onClick={handleReplace}>
                Replace
              </Button>
            </div>
          </div>
        </div>
      )
        : null}
    </div>
  );
}

const SPECIAL_CHARACTERS = [
  ['€', '£', '¢', '$', '§', '∞'],
  ['Ω', '@', '¶', '&', '#', '≠'],
  ['æ', '¥', 'ø', 'ß', 'π', 'μ'],
  ['%', '‰', '÷', '[', ']', '±'],
  ['!', '˚', '›', 'ˆ', '·', '™'],
  ['∑', 'Œ', '®', 'Æ', '˙', '∆'],
  ['¬', '‡', 'ƒ', '∏', 'Å', '∫'],
  ['√', '©', '«', 'å', '∂', 'ˇ'],
  ['¯', "'", '¸', '˜', '‹', '¨'],
  ['°', 'ı', '†', '"', '&', '/'],
  ['(', ')', '=', '?', '*', '`'],
  ['|', '{', '}', '—', 'œ', '°'],
];

function SpecialCharactersGrid({
  handleInsertSpecialCharacter
}: Readonly<{
  handleInsertSpecialCharacter: (char: string) => void;
}>): JSX.Element {

  const handleClick = useCallback((char: string) => {
    handleInsertSpecialCharacter(char);
  }, [handleInsertSpecialCharacter]);

  return (
    <div className="min-w-[260px] max-h-[140px] rounded border border-gray-300 bg-white shadow overflow-y-scroll overflow-x-clip">
      <div className="grid grid-cols-6">
        {SPECIAL_CHARACTERS.flat().map((char, idx) => (
          <button
            key={`${char}-${idx}`}
            className="flex h-12 w-12 items-center justify-center text-2xl text-blue-900 hover:bg-gray-100"
            onClick={() => handleClick(char)}
          >
            {char}
          </button>
        ))}
      </div>
    </div>
  );
};

interface ToolButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  tooltip?: string;
  ref?: React.Ref<HTMLButtonElement>;
}

function ToolButton(props: Readonly<ToolButtonProps>): JSX.Element {
  const { className, tooltip, children, ref, ...restOfProps } = props

  return (
    <SystemTooltip
      message={tooltip}
      placement='bottom'
    >
      <button
        type="button"
        className={clsx("toolbar-item spaced", className)}
        ref={ref}
        {...restOfProps}
      >
        {children}
      </button>
    </SystemTooltip>
  )
}

export function ToolbarPluginTools({
  editor,
  setIsLinkEditMode,
  position,
  editorKey
}: Readonly<{
  editor: LexicalEditor;
  position: 'floatingToolbar' | 'fixedToolbar'
  editorKey: EditorKey;
  setIsLinkEditMode: Dispatch<boolean>;
}>): JSX.Element {
  const {
    settings: {
      [editorKey]: {
        [position]: {
          findAndReplace: {
            show: showFindAndReplace
          },
          specialCharacters: {
            show: showSpecialCharacters
          },
          underline: {
            show: showUnderline
          },
          fontSize: {
            show: showFontSize
          }
        }
      }
    },
  } = useEditorSettings();

  const { toolbarState } = useToolbarState();
  const [isEditable] = useState(() => editor.isEditable());

  const insertLink = useCallback(() => {
    if (!toolbarState.isLink) {
      setIsLinkEditMode(true);
      editor.dispatchCommand(
        TOGGLE_LINK_COMMAND,
        { url: sanitizeUrl(''), target: '_blank' },
      );
    } else {
      setIsLinkEditMode(false);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, setIsLinkEditMode, toolbarState.isLink]);

  const insertSpecialCharacter = useCallback(
    (char: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertText(char);
        }
      })
    },
    [editor]
  );

  return (
    <>
      {showFontSize && (
        <SystemTooltip
          message='Text size'
          placement='bottom'
        >
          <FontSizeDropDown
            editor={editor}
            disabled={!isEditable}
            selectionFontSize={toolbarState.fontSize.slice(0, -2) as keyof typeof fontSizes}
          />
        </SystemTooltip>
      )}

      <ToolButton
        tooltip={`'Undo - ${IS_APPLE ? 'Undo (⌘Z)' : 'Undo (Ctrl+Z)'}`}
        disabled={!toolbarState.canUndo || !isEditable}
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        // title={IS_APPLE ? 'Undo (⌘Z)' : 'Undo (Ctrl+Z)'}
        aria-label="Undo"
      >
        <FaUndoAlt size={18} />
      </ToolButton>

      <ToolButton
        tooltip={`Redo - ${IS_APPLE ? 'Redo (⇧⌘Z)' : 'Redo (Ctrl+Y)'}`}
        disabled={!toolbarState.canRedo || !isEditable}
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        // title={IS_APPLE ? 'Redo (⇧⌘Z)' : 'Redo (Ctrl+Y)'}
        aria-label="Redo">
        <FaRedoAlt size={18} />
      </ToolButton>

      <SystemTooltip
        message='Text styles'
        placement='bottom'
      >
        <BlockHeadingDropDown
          editor={editor}
          disabled={!isEditable}
          blockType={toolbarState.blockType}
        />
      </SystemTooltip>

      <ToolButton
        tooltip={`Bold - ${SHORTCUTS.BOLD}`}
        disabled={!isEditable}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
        className={(toolbarState.isBold ? 'active' : '')}
        // title={`Bold (${SHORTCUTS.BOLD})`}
        aria-label={`Format text as bold. Shortcut: ${SHORTCUTS.BOLD}`}>
        <MdFormatBold size={24} />
      </ToolButton>

      <ToolButton
        tooltip={`Italic - ${SHORTCUTS.ITALIC}`}
        disabled={!isEditable}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        }}
        className={(toolbarState.isItalic ? 'active' : '')}
        // title={`Italic (${SHORTCUTS.ITALIC})`}
        aria-label={`Format text as italics. Shortcut: ${SHORTCUTS.ITALIC}`}>
        <MdFormatItalic size={24} />
      </ToolButton>

      {showUnderline && (
        <ToolButton
          tooltip={`Underline - ${SHORTCUTS.UNDERLINE}`}
          disabled={!isEditable}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
          }}
          className={(toolbarState.isUnderline ? 'active' : '')}
          // title={`Underline (${SHORTCUTS.UNDERLINE})`}
          aria-label={`Format text to underlined. Shortcut: ${SHORTCUTS.UNDERLINE}`}>
          <MdFormatUnderlined size={24} />
        </ToolButton>
      )}

      {showSpecialCharacters && (
        <SystemPopover
          trigger={{
            component: (
              <ToolButton
                tooltip='Special characters'
                disabled={!isEditable}
                // title={`Add a special character.`}
                aria-label={`Add a special character.`}
              >
                <RiOmega size={24} />
              </ToolButton>
            ),
            disabled: !isEditable
          }}
          content={{
            component: (
              <SpecialCharactersGrid
                handleInsertSpecialCharacter={insertSpecialCharacter}
              />
            )
          }}
        />
      )}

      <ElementAlignDropdown
        editor={editor}
        disabled={!isEditable}
        isRTL={toolbarState.isRTL}
        value={toolbarState.elementFormat as USED_ELEMENT_ALIGN_TYPES}
      />

      <ToolButton
        tooltip={`Bullet List - ${SHORTCUTS.BULLET_LIST}`}
        disabled={!isEditable}
        onClick={() => formatBulletList(editor, toolbarState.blockType)}
      // title={`Bullet List (${SHORTCUTS.BULLET_LIST})`}
      >
        <MdFormatListBulleted size={24} />
      </ToolButton>

      <ToolButton
        tooltip={`Numbered List - ${SHORTCUTS.NUMBERED_LIST}`}
        disabled={!isEditable}
        onClick={() => formatNumberedList(editor, toolbarState.blockType)}
      // title={`Numbered List (${SHORTCUTS.NUMBERED_LIST})`}
      >
        <MdFormatListNumbered size={24} />
      </ToolButton>

      <ToolButton
        tooltip={`Add link - ${SHORTCUTS.INSERT_LINK}`}
        disabled={!isEditable}
        onClick={insertLink}
        className={(toolbarState.isLink ? 'active' : '')}
        aria-label="Add link"
      // title={`Add link (${SHORTCUTS.INSERT_LINK})`}
      >
        <MdInsertLink size={24} />
      </ToolButton>

      {showFindAndReplace && (
        <FindAndReplaceButton
          editor={editor}
          disabled={!isEditable}
        />
      )}
    </>
  )
}

export function ToolbarPlugin({
  editor,
  setIsLinkEditMode,
  editorKey
}: Readonly<{
  editor: LexicalEditor;
  setIsLinkEditMode: Dispatch<boolean>;
  editorKey: EditorKey;
}>): JSX.Element {
  const {
    settings: {
      [editorKey]: {
        fixedToolbar: {
          topPosition = 'top-0'
        }
      }
    },
  } = useEditorSettings();

  const { updateToolbarState } = useToolbarState();

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      updateToolbarState('isImageCaption', false);

      const anchorNode = selection.anchor.getNode();

      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
            const parent = e.getParent();
            return parent !== null && $isRootOrShadowRoot(parent);
          });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      updateToolbarState('isRTL', $isParentElementRTL(selection));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      const isLink = $isLinkNode(parent) || $isLinkNode(node);
      updateToolbarState('isLink', isLink);

      const tableNode = $findMatchingParent(node, $isTableNode);
      if ($isTableNode(tableNode)) {
        updateToolbarState('rootType', 'table');
      } else {
        updateToolbarState('rootType', 'root');
      }

      if (elementDOM !== null) {
        // setSelectedElementKey(elementKey);
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(
            anchorNode,
            ListNode,
          );
          const type = parentList
            ? parentList.getListType()
            : element.getListType();

          updateToolbarState('blockType', type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          if (type in blockTypeToBlockName) {
            updateToolbarState(
              'blockType',
              type as keyof typeof blockTypeToBlockName,
            );
          }
          if ($isCodeNode(element)) {
            const language =
              element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP;
            updateToolbarState(
              'codeLanguage',
              language ? CODE_LANGUAGE_MAP[language] || language : '',
            );
            return;
          }
        }
      }
      // Handle buttons
      updateToolbarState(
        'fontColor',
        $getSelectionStyleValueForProperty(selection, 'color', '#000'),
      );
      updateToolbarState(
        'bgColor',
        $getSelectionStyleValueForProperty(
          selection,
          'background-color',
          '#fff',
        ),
      );
      updateToolbarState(
        'fontFamily',
        $getSelectionStyleValueForProperty(selection, 'font-family', 'Arial'),
      );
      let matchingParent;
      if ($isLinkNode(parent)) {
        // If node is a link, we need to fetch the parent paragraph node to set format
        matchingParent = $findMatchingParent(
          node,
          (parentNode) => $isElementNode(parentNode) && !parentNode.isInline(),
        );
      }

      // If matchingParent is a valid node, pass it's format type
      updateToolbarState(
        'elementFormat',
        $isElementNode(matchingParent)
          ? matchingParent.getFormatType()
          : $isElementNode(node)
            ? node.getFormatType()
            : parent?.getFormatType() || 'left',
      );
    }
    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      // Update text format
      updateToolbarState('isBold', selection.hasFormat('bold'));
      updateToolbarState('isItalic', selection.hasFormat('italic'));
      updateToolbarState('isUnderline', selection.hasFormat('underline'));
      updateToolbarState(
        'isStrikethrough',
        selection.hasFormat('strikethrough'),
      );
      updateToolbarState('isSubscript', selection.hasFormat('subscript'));
      updateToolbarState('isSuperscript', selection.hasFormat('superscript'));
      updateToolbarState('isHighlight', selection.hasFormat('highlight'));
      updateToolbarState('isCode', selection.hasFormat('code'));
      updateToolbarState(
        'fontSize',
        $getSelectionStyleValueForProperty(selection, 'font-size', '15px'),
      );
      updateToolbarState('isLowercase', selection.hasFormat('lowercase'));
      updateToolbarState('isUppercase', selection.hasFormat('uppercase'));
      updateToolbarState('isCapitalize', selection.hasFormat('capitalize'));
    }
  }, [editor, updateToolbarState]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload) => {
        $updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, $updateToolbar]);

  useEffect(() => {
    // editor.getEditorState().read(() => {
    //   $updateToolbar();
    // });
    editor.read(() => {
      $updateToolbar();
    });
  }, [editor, $updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      // editor.registerEditableListener((editable) => {
      //   setIsEditable(editable);
      // }),
      editor.registerUpdateListener(() => {
        // editorState.read(() => {
        //   $updateToolbar();
        // });
        editor.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand<boolean>(
        CAN_UNDO_COMMAND,
        (payload) => {
          updateToolbarState('canUndo', payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand<boolean>(
        CAN_REDO_COMMAND,
        (payload) => {
          updateToolbarState('canRedo', payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),

      editor.registerCommand(
        INSERT_HORIZONTAL_RULE_COMMAND,
        () => {
          const selection = $getSelection();

          if (!$isRangeSelection(selection)) {
            return false;
          }

          const focusNode = selection.focus.getNode();
          if (focusNode !== null) {
            const rule = $createHorizontalRuleNode();
            $insertNodeToNearestRoot(rule);
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    );
  }, [$updateToolbar, editor, updateToolbarState]);

  return (
    <div
      className={clsx(
        "toolbar z-editor-toolbar border-t border-t-cs-300 bg-cs-200",
        topPosition
      )}
    >
      <ToolbarPluginTools
        editor={editor}
        setIsLinkEditMode={setIsLinkEditMode}
        editorKey={editorKey}
        position='fixedToolbar'
      />
    </div>
  );
}
